import { useState, useEffect } from 'react';
import { Target, Calendar } from 'lucide-react';
import { CalculatorCard, SliderInput } from '../CalculatorCard';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

export const SavingsGoalCalculator = () => {
  const [targetAmount, setTargetAmount] = useState(500000);
  const [currentSavings, setCurrentSavings] = useState(50000);
  const [monthlySavings, setMonthlySavings] = useState(5000);
  const [interestRate, setInterestRate] = useState(4);
  
  const [monthsToGoal, setMonthsToGoal] = useState(0);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    const monthlyRate = interestRate / 100 / 12;
    let balance = currentSavings;
    let months = 0;
    const maxMonths = 30 * 12;
    
    const data = [{ month: 0, celkem: currentSavings }];

    while (balance < targetAmount && months < maxMonths) {
      balance = balance * (1 + monthlyRate) + monthlySavings;
      months++;
      
      if (months % 12 === 0 || balance >= targetAmount) {
        data.push({ month: months, celkem: Math.round(balance) });
      }
    }

    setMonthsToGoal(months < maxMonths ? months : -1);
    setChartData(data);
  }, [targetAmount, currentSavings, monthlySavings, interestRate]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-navy border border-white/10 rounded-lg p-3 shadow-lg">
          <p className="text-white font-semibold">{Math.round(label / 12)} let</p>
          <p className="text-green font-bold">{payload[0]?.value?.toLocaleString('cs-CZ')} Kč</p>
        </div>
      );
    }
    return null;
  };

  const years = monthsToGoal > 0 ? Math.floor(monthsToGoal / 12) : 0;
  const remainingMonths = monthsToGoal > 0 ? monthsToGoal % 12 : 0;

  const result = (
    <div className="space-y-6">
      <div className="text-right">
        <p className="text-gray-400 text-sm">Cíle dosáhnete za</p>
        <p className="text-4xl font-bold text-green">
          {monthsToGoal > 0 
            ? `${years > 0 ? `${years} let` : ''} ${remainingMonths > 0 ? `${remainingMonths} měs.` : ''}`
            : '30+ let'}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Cílová částka</p>
          <p className="text-white font-bold text-lg">{targetAmount.toLocaleString('cs-CZ')} Kč</p>
        </div>
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Měsíčně spoříte</p>
          <p className="text-green font-bold text-lg">{monthlySavings.toLocaleString('cs-CZ')} Kč</p>
        </div>
      </div>

      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorSavings" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22C55E" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#22C55E" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="month" 
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
              tickFormatter={(v) => `${Math.round(v / 12)}r`}
            />
            <YAxis 
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
              tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine 
              y={targetAmount} 
              stroke="#EAB308" 
              strokeDasharray="5 5"
            />
            <Area 
              type="monotone" 
              dataKey="celkem" 
              stroke="#22C55E" 
              fill="url(#colorSavings)" 
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  return (
    <CalculatorCard
      id="sporeni"
      title="Spořicí kalkulačka"
      subtitle="Kdy dosáhnete svého cíle?"
      icon={Target}
      tip="Automatické spoření je klíč k úspěchu. Nastavte si trvalý příkaz hned po výplatě – co nevidíte, to neutratíte. Lidé s konkrétním cílem spoří 2× efektivněji."
      result={result}
      showResult={true}
      testId="savings-goal-calculator"
    >
      <SliderInput
        label="Cílová částka"
        value={targetAmount}
        onChange={setTargetAmount}
        min={50000}
        max={5000000}
        step={50000}
        suffix="Kč"
        testId="savings-target-slider"
      />
      <SliderInput
        label="Již naspořeno"
        value={currentSavings}
        onChange={setCurrentSavings}
        min={0}
        max={1000000}
        step={10000}
        suffix="Kč"
        testId="savings-current-slider"
      />
      <SliderInput
        label="Měsíční spoření"
        value={monthlySavings}
        onChange={setMonthlySavings}
        min={500}
        max={50000}
        step={500}
        suffix="Kč"
        testId="savings-monthly-slider"
      />
      <SliderInput
        label="Úroková sazba"
        value={interestRate}
        onChange={setInterestRate}
        min={0}
        max={10}
        step={0.5}
        suffix="%"
        formatValue={(v) => `${v.toFixed(1)} %`}
        testId="savings-rate-slider"
      />
    </CalculatorCard>
  );
};
